/**
 * SafetyTab Component
 * 
 * Main Safety tab container within a project.
 * Shows a list of safety assessments and allows creating new ones.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Shield, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { SafetyAssessmentList } from './SafetyAssessmentList';
import { SSADashboard } from './SSADashboard';
import { safetyApi } from '@/services/safetyApi';
import type { SafetyAssessment } from '@aerotage/shared';

interface SafetyTabProps {
  projectId?: string;
}

export const SafetyTab: React.FC<SafetyTabProps> = ({ projectId: propProjectId }) => {
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const projectId = propProjectId || routeProjectId;

  const [assessments, setAssessments] = useState<SafetyAssessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<SafetyAssessment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (projectId) {
      loadAssessments();
    }
  }, [projectId]);

  const loadAssessments = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await safetyApi.listAssessments(projectId);
      setAssessments(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assessments');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAssessment = async (systemName: string, systemDescription?: string) => {
    if (!projectId) return;
    
    try {
      const newAssessment = await safetyApi.createAssessment(projectId, {
        systemName,
        systemDescription,
      });
      setAssessments([newAssessment, ...assessments]);
      setShowCreateModal(false);
      setSelectedAssessment(newAssessment);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create assessment');
    }
  };

  const handleSelectAssessment = (assessment: SafetyAssessment) => {
    setSelectedAssessment(assessment);
  };

  const handleBackToList = () => {
    setSelectedAssessment(null);
    loadAssessments(); // Refresh in case of updates
  };

  // Show dashboard if an assessment is selected
  if (selectedAssessment) {
    return (
      <SSADashboard
        assessment={selectedAssessment}
        onBack={handleBackToList}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">Safety Assessments</h1>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New SSA
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-4" />
              <p className="text-muted-foreground">{error}</p>
              <Button onClick={loadAssessments} variant="secondary" className="mt-4">
                Retry
              </Button>
            </div>
          </div>
        ) : assessments.length === 0 ? (
          <EmptyState
            title="No Safety Assessments"
            description="Create a system safety assessment to get started with your safety analysis."
            action={{
              label: 'Create Assessment',
              onClick: () => setShowCreateModal(true),
            }}
          />
        ) : (
          <SafetyAssessmentList
            assessments={assessments}
            onSelect={handleSelectAssessment}
          />
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateAssessmentModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateAssessment}
        />
      )}
    </div>
  );
};

// Create modal component
interface CreateAssessmentModalProps {
  onClose: () => void;
  onCreate: (systemName: string, systemDescription?: string) => void;
}

const CreateAssessmentModal: React.FC<CreateAssessmentModalProps> = ({
  onClose,
  onCreate,
}) => {
  const [systemName, setSystemName] = useState('');
  const [systemDescription, setSystemDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!systemName.trim()) return;
    
    setIsSubmitting(true);
    await onCreate(systemName.trim(), systemDescription.trim() || undefined);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Create Safety Assessment
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="systemName" className="block text-sm font-medium text-muted-foreground mb-1">
                System Name *
              </Label>
              <Input
                id="systemName"
                type="text"
                value={systemName}
                onChange={(e) => setSystemName(e.target.value)}
                placeholder="e.g., Navigation Display Unit"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="systemDescription" className="block text-sm font-medium text-muted-foreground mb-1">
                Description
              </Label>
              <Textarea
                id="systemDescription"
                value={systemDescription}
                onChange={(e) => setSystemDescription(e.target.value)}
                placeholder="Brief description of the system and its boundaries..."
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" disabled={!systemName.trim() || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Assessment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
